/**
 * Dev-only overlay showing UX profile + intent + policy when UX_DEBUG_OVERLAY_ENABLED=1.
 * No UI impact when disabled (default) or in production.
 */

import { useEffect, useState } from "react";
import { getUxFlags } from "@/lib/uxFlags";

const SESSION_ID_KEY = "ux_session_id";
const POLL_MS = 4000;

interface UxProfileResponse {
  session_id: string;
  profile: "anxious" | "rational" | "fast";
  intent: { price_focus: number; trust_needed: number; fast_decision: number };
  serviceIntent?: string;
  counters: Record<string, number>;
  lastSeenTs: number;
}

interface AllowedPolicy {
  ctaVariant?: string;
  homeVariant?: string;
  servicesOrder?: string[];
  servicesSpotlight?: string;
  faqVariant?: string;
  confidence?: number;
}

interface PolicyResponse {
  policy: AllowedPolicy;
  source: "openclaw" | "deterministic";
  validationNotes?: string[];
}

function fmt(n: number) {
  return n.toFixed(2);
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-amber-600/80">{label}:</span>{" "}
      <span className="text-white">{value}</span>
    </div>
  );
}

export function UxDebugOverlay() {
  const [show, setShow] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [profile, setProfile] = useState<UxProfileResponse | null>(null);
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let mounted = true;
    getUxFlags().then((flags) => {
      if (!mounted || !flags.uxDebugOverlayEnabled) return;
      setShow(true);
      setShowPolicy(flags.uxPolicyEnabled);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!show) return;
    const sessionId = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SESSION_ID_KEY) : null;
    if (!sessionId) return;

    const fetchProfile = () => {
      fetch(`/api/ux-profile?session_id=${encodeURIComponent(sessionId)}`, { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => data && setProfile(data as UxProfileResponse))
        .catch(() => {});
    };

    const fetchPolicy = () => {
      if (!showPolicy) return;
      fetch(`/api/ux-policy?session_id=${encodeURIComponent(sessionId)}`, { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => data && setPolicy(data as PolicyResponse))
        .catch(() => {});
    };

    fetchProfile();
    fetchPolicy();
    const t = setInterval(() => { fetchProfile(); fetchPolicy(); }, POLL_MS);
    return () => clearInterval(t);
  }, [show, showPolicy]);

  if (!show || !import.meta.env.DEV) return null;
  if (!profile) return null;

  return (
    <div
      className="fixed bottom-3 right-3 z-[9999] max-w-[300px] rounded border border-amber-600/60 bg-black/90 px-3 py-2 font-mono text-xs text-amber-200 shadow-lg select-none"
      aria-hidden
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-amber-400">UX Debug</span>
        <button
          className="text-amber-600 hover:text-amber-300 ml-2"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? "▲" : "▼"}
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-1.5">
          {/* Profile panel */}
          <div className="border-t border-amber-900/50 pt-1">
            <div className="text-amber-500 text-[10px] uppercase tracking-wide mb-0.5">Profile</div>
            <div className="space-y-0.5">
              <Row label="profile" value={profile.profile} />
              <Row
                label="intent"
                value={`price=${fmt(profile.intent.price_focus)} trust=${fmt(profile.intent.trust_needed)} fast=${fmt(profile.intent.fast_decision)}`}
              />
              {profile.serviceIntent && (
                <Row label="serviceIntent" value={profile.serviceIntent} />
              )}
            </div>
          </div>

          {/* Policy panel */}
          {policy && (
            <div className="border-t border-amber-900/50 pt-1">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-amber-500 text-[10px] uppercase tracking-wide">Policy</span>
                <span
                  className={`text-[9px] px-1 rounded ${
                    policy.source === "openclaw"
                      ? "bg-green-900/60 text-green-400"
                      : "bg-slate-800 text-slate-400"
                  }`}
                >
                  {policy.source}
                </span>
                {policy.policy.confidence !== undefined && (
                  <span className="text-slate-500 text-[9px]">
                    {(policy.policy.confidence * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="space-y-0.5">
                {policy.policy.ctaVariant && (
                  <Row label="cta" value={policy.policy.ctaVariant} />
                )}
                {policy.policy.homeVariant && (
                  <Row label="layout" value={policy.policy.homeVariant} />
                )}
                {policy.policy.servicesOrder && (
                  <Row label="services" value={policy.policy.servicesOrder.join("→")} />
                )}
                {policy.policy.servicesSpotlight && (
                  <Row label="spotlight" value={policy.policy.servicesSpotlight} />
                )}
                {policy.policy.faqVariant && (
                  <Row label="faq" value={policy.policy.faqVariant} />
                )}
                {policy.validationNotes && policy.validationNotes.length > 0 && (
                  <div className="text-slate-500 text-[9px] mt-0.5 leading-tight">
                    {policy.validationNotes.map((n, i) => (
                      <div key={i}>⚠ {n}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
